import React, { Component } from 'react';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import fileDownload from 'js-file-download';
import * as XLSX from 'xlsx';
import "react-datepicker/dist/react-datepicker.css";

import { expenseBALService } from '../../bal/expense.bal';
import ToastMessage from "../deals/components/toastMessage";

class ExpenseDashboard extends Component {
    constructor(props) {
        super(props);

        const previousMonth = moment().subtract(1, 'month');
        this.state = {
            periodType: 'monthly',
            selectedMonth: previousMonth.toDate(),
            selectedQuarter: previousMonth.quarter(),
            selectedQuarterYear: previousMonth.year(),
            selectedYear: previousMonth.year(),
            recordStartDate: previousMonth.startOf('month').toDate(),
            recordEndDate: previousMonth.endOf('month').toDate(),
            files: [],
            isGenerating: false,
            isLoadingFiles: false,
            isLoadingPreview: false,
            isDownloadingPreview: false,
            pendingDownload: null,
            selectedHistoryFileName: null,
            previewSheetIndex: 0,
            showMessage: false,
            errorList: [],
            messageHeader: ''
        };

        this.getHistory = this.getHistory.bind(this);
        this.generate = this.generate.bind(this);
        this.closeMessage = this.closeMessage.bind(this);
        this.setPeriodType = this.setPeriodType.bind(this);
        this.handleMonthChange = this.handleMonthChange.bind(this);
        this.handleQuarterChange = this.handleQuarterChange.bind(this);
        this.handleYearChange = this.handleYearChange.bind(this);
        this.openHistoryPreview = this.openHistoryPreview.bind(this);
        this.clearDownloadPreview = this.clearDownloadPreview.bind(this);
        this.confirmDownloadPreview = this.confirmDownloadPreview.bind(this);
        this.setPreviewSheetIndex = this.setPreviewSheetIndex.bind(this);
    }

    componentDidMount() {
        this.getHistory();
    }

    closeMessage() {
        this.setState({ showMessage: false, errorList: [], messageHeader: '' });
    }

    formatHongKongDateTime(value) {
        if (!value) {
            return '';
        }
        const raw = String(value).trim();

        const utcWithSeconds = moment.utc(raw, "YYYY-MM-DD HH:mm:ss [UTC]", true);
        if (utcWithSeconds.isValid()) {
            return utcWithSeconds.utcOffset(8).format("YYYY-MM-DD HH:mm");
        }

        const utcNoSeconds = moment.utc(raw, "YYYY-MM-DD HH:mm [UTC]", true);
        if (utcNoSeconds.isValid()) {
            return utcNoSeconds.utcOffset(8).format("YYYY-MM-DD HH:mm");
        }

        const local = moment(raw, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm"], true);
        if (local.isValid()) {
            return local.format("YYYY-MM-DD HH:mm");
        }

        return raw;
    }

    readBlobAsArrayBuffer(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = function () {
                resolve(reader.result);
            };
            reader.onerror = function () {
                reject(reader.error || new Error('Unable to read file preview.'));
            };
            reader.readAsArrayBuffer(blob);
        });
    }

    buildExcelPreview(blob) {
        return this.readBlobAsArrayBuffer(blob).then((buffer) => {
            const workbook = XLSX.read(buffer, { type: 'array' });
            const sheets = workbook.SheetNames.map((sheetName) => {
                const worksheet = workbook.Sheets[sheetName];
                const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
                const headerRowIndex = this.detectHeaderRowIndex(rows);
                const columnCount = rows.reduce((max, row) => Math.max(max, row.length), 0);

                return {
                    name: sheetName,
                    range: worksheet['!ref'] || '',
                    rowCount: rows.length,
                    columnCount: columnCount,
                    headerRowIndex: headerRowIndex,
                    rows: rows.slice(0, 25)
                };
            });

            return {
                sheetNames: workbook.SheetNames,
                sheets: sheets
            };
        });
    }

    detectHeaderRowIndex(rows) {
            if (!rows || rows.length === 0) {
                return 0;
            }

            const scanLimit = Math.min(rows.length, 10);
            let bestIndex = 0;
            let bestScore = -Infinity;

            for (let rowIndex = 0; rowIndex < scanLimit; rowIndex += 1) {
                const row = rows[rowIndex] || [];
                let nonEmptyCount = 0;
                let textCount = 0;
                let numericCount = 0;

                row.forEach((cell) => {
                    if (cell === null || cell === undefined || cell === '') {
                        return;
                    }

                    nonEmptyCount += 1;
                    if (typeof cell === 'number') {
                        numericCount += 1;
                        return;
                    }

                    const value = String(cell).trim();
                    if (/^-?\d+(\.\d+)?$/.test(value)) {
                        numericCount += 1;
                    } else {
                        textCount += 1;
                    }
                });

                const score = (nonEmptyCount * 10) + (textCount * 4) - (numericCount * 2) - rowIndex;
                if (nonEmptyCount > 1 && score > bestScore) {
                    bestScore = score;
                    bestIndex = rowIndex;
                }
            }

            return bestIndex;
    }

    parseDateRangeFromText(value) {
        if (!value) {
            return null;
        }

        const matches = String(value).match(/\d{4}[-/]\d{2}[-/]\d{2}/g);
        if (!matches || matches.length < 2) {
            return null;
        }

        const start = moment(matches[0], ["YYYY-MM-DD", "YYYY/MM/DD"], true);
        const end = moment(matches[1], ["YYYY-MM-DD", "YYYY/MM/DD"], true);
        if (!start.isValid() || !end.isValid()) {
            return null;
        }

        return { start, end };
    }

    getPeriodMeta(startDate, endDate) {
        const start = moment(startDate);
        const end = moment(endDate);

        if (!start.isValid() || !end.isValid()) {
            return { periodType: 'custom', periodLabel: 'Custom' };
        }

        if (
            start.clone().startOf('month').isSame(start, 'day') &&
            end.clone().endOf('month').isSame(end, 'day') &&
            start.isSame(end, 'month')
        ) {
            return { periodType: 'monthly', periodLabel: start.format('MMM YYYY') };
        }

        if (
            start.clone().startOf('quarter').isSame(start, 'day') &&
            end.clone().endOf('quarter').isSame(end, 'day') &&
            start.isSame(end, 'quarter') &&
            start.isSame(end, 'year')
        ) {
            return { periodType: 'quarterly', periodLabel: `Q${start.quarter()} ${start.format('YYYY')}` };
        }

        if (
            start.clone().startOf('year').isSame(start, 'day') &&
            end.clone().endOf('year').isSame(end, 'day') &&
            start.isSame(end, 'year')
        ) {
            return { periodType: 'yearly', periodLabel: start.format('YYYY') };
        }

        return { periodType: 'custom', periodLabel: `${start.format('YYYY-MM-DD')} to ${end.format('YYYY-MM-DD')}` };
    }

    formatRecordPeriodDisplay(recordPeriod, startDate, endDate) {
        const parsedRange = this.parseDateRangeFromText(recordPeriod);
        const start = parsedRange ? parsedRange.start : moment(startDate);
        const end = parsedRange ? parsedRange.end : moment(endDate);
        if (!start.isValid() || !end.isValid()) {
            return recordPeriod || '';
        }
        const { periodType, periodLabel } = this.getPeriodMeta(start, end);
        const typeText = periodType.charAt(0).toUpperCase() + periodType.slice(1);
        return `${typeText} (${periodLabel})`;
    }

    getPeriodTypeFromText(value) {
        const text = String(value || '').toLowerCase();
        if (text.indexOf('quarter') >= 0 || /^q[1-4]/.test(text)) {
            return 'quarterly';
        }
        if (text.indexOf('year') >= 0) {
            return 'yearly';
        }
        if (text.indexOf('month') >= 0) {
            return 'monthly';
        }
        return 'custom';
    }

    getReportFileName(periodType, startDate, originalFileName) {
        const start = moment(startDate);
        const extensionMatch = (originalFileName || '').match(/(\.[^./\\]+)$/);
        const extension = extensionMatch ? extensionMatch[1] : '.xlsx';
        if (!start.isValid()) {
            return originalFileName || `expense_report${extension}`;
        }

        if (periodType === 'monthly') {
            return `expense_report_monthly_${start.format('YYYY-MM')}${extension}`;
        }
        if (periodType === 'quarterly') {
            return `expense_report_quarterly_${start.format('YYYY')}_Q${start.quarter()}${extension}`;
        }
        if (periodType === 'yearly') {
            return `expense_report_yearly_${start.format('YYYY')}${extension}`;
        }

        return `expense_report_${start.format('YYYY-MM-DD')}${extension}`;
    }

    getHistoryReportFileName(file, originalFileName) {
        const parsedRange = this.parseDateRangeFromText(file?.recordPeriod);
        const start = parsedRange ? parsedRange.start : moment(file?.recordStartDate);
        const end = parsedRange ? parsedRange.end : moment(file?.recordEndDate);
        if (!start.isValid() || !end.isValid()) {
            return originalFileName;
        }
        const { periodType } = this.getPeriodMeta(start, end);

        return this.getReportFileName(periodType, start, originalFileName);
    }

    getPreviewTitle(pendingDownload) {
        if (!pendingDownload) {
            return 'Download Preview';
        }

        if (pendingDownload.source === 'generated') {
            return 'Generated Report Preview';
        }

        return 'History File Preview';
    }

    getPreviewRows(pendingDownload) {
        if (!pendingDownload) {
            return [];
        }

        const sourceData = pendingDownload.sourceData || {};
        const combined = {
            ...sourceData,
            displayFileName: pendingDownload.displayFileName,
            displayRecordPeriod: pendingDownload.displayRecordPeriod,
            originalFileName: pendingDownload.originalFileName,
            periodType: pendingDownload.periodType,
            recordStartDate: pendingDownload.recordStartDate,
            recordEndDate: pendingDownload.recordEndDate,
            version: pendingDownload.version,
            reportGeneratedDate: pendingDownload.reportGeneratedDate
        };
        const keysToSkip = {
            blobData: true,
            sourceData: true,
            reportGeneratedDateRaw: true,
            downloadDateRaw: true
        };
        const orderedRows = [
            ['Source', pendingDownload.source === 'generated' ? 'Generated report' : 'History file'],
            ['File Name', combined.displayFileName || combined.fileName || '-'],
            ['Record Period', combined.displayRecordPeriod || combined.recordPeriod || '-'],
            ['Period Type', combined.periodType ? combined.periodType.charAt(0).toUpperCase() + combined.periodType.slice(1) : '-'],
            ['Version', combined.version || '-'],
            ['Record Start Date', combined.recordStartDate ? moment(combined.recordStartDate).format('YYYY-MM-DD') : '-'],
            ['Record End Date', combined.recordEndDate ? moment(combined.recordEndDate).format('YYYY-MM-DD') : '-'],
            ['Download Date', combined.downloadDate || '-'],
            ['Report Generated Date', combined.reportGeneratedDate || '-'],
            ['Original File Name', combined.originalFileName || '-']
        ];

        const knownLabels = {};
        orderedRows.forEach(row => {
            knownLabels[row[0]] = true;
        });

        Object.keys(combined).forEach(key => {
            if (keysToSkip[key]) {
                return;
            }

            const label = key.replace(/([a-z])([A-Z])/g, '$1 $2');
            const prettyLabel = label.charAt(0).toUpperCase() + label.slice(1);
            if (knownLabels[prettyLabel]) {
                return;
            }

            const value = combined[key];
            if (value === null || value === undefined || value === '') {
                return;
            }

            let displayValue = value;
            if (moment.isMoment(displayValue) || displayValue instanceof Date) {
                displayValue = moment(displayValue).format('YYYY-MM-DD HH:mm');
            } else if (typeof displayValue === 'object') {
                displayValue = JSON.stringify(displayValue, null, 2);
            }

            orderedRows.push([prettyLabel, displayValue]);
        });

        return orderedRows;
    }

    setPreviewSheetIndex(index) {
        const { pendingDownload } = this.state;
        if (!pendingDownload || !pendingDownload.previewData) {
            return;
        }

        this.setState({
            pendingDownload: {
                ...pendingDownload,
                previewSheetIndex: index,
            }
        });
    }

    getActivePreviewSheet(pendingDownload) {
        if (!pendingDownload || !pendingDownload.previewData || !pendingDownload.previewData.sheets || pendingDownload.previewData.sheets.length === 0) {
            return null;
        }

        const index = pendingDownload.previewSheetIndex || 0;
        return pendingDownload.previewData.sheets[index] || pendingDownload.previewData.sheets[0];
    }

    shouldHidePreviewColumn(headerValue) {
        const text = String(headerValue || '').toLowerCase();
        return text.indexOf('expense id') >= 0 || text.indexOf('expenseid') >= 0;
    }

    isPercentageHeader(headerValue) {
        const text = String(headerValue || '').toLowerCase();
        return text.indexOf('%') >= 0 || text.indexOf('percent') >= 0 || text.indexOf('percentage') >= 0 || text.indexOf('pct') >= 0;
    }

    isFxRateHeader(headerValue) {
        const text = String(headerValue || '').toLowerCase();
        return text.indexOf('fx rate') >= 0 || text.indexOf('exchange rate') >= 0 || text === 'fx rate';
    }

    isDateLikeHeader(headerValue) {
        const text = String(headerValue || '').toLowerCase();
        return text.indexOf('date') >= 0 || text.indexOf('time') >= 0 || text.indexOf('posted') >= 0 || text.indexOf('generated') >= 0 || text.indexOf('download') >= 0 || text.indexOf('month') >= 0 || text.indexOf('year') >= 0;
    }

    getPreviewColumnWidth(headerValue, sheetName) {
        const text = String(headerValue || '').toLowerCase();
        const sheetText = String(sheetName || '').toLowerCase();
        const isExpensesSheet = sheetText.indexOf('expense') >= 0 && sheetText.indexOf('split') < 0;
        const isSplitDetailsSheet = sheetText.indexOf('split') >= 0;

        if (text === 'fx report month' || text.indexOf('fx report month') >= 0) {
            return 100;
        }

        if (isExpensesSheet) {
            if (text === 'date') {
                return 96;
            }
            if (text === 'currency') {
                return 72;
            }
            if (text === 'amount') {
                return 88;
            }
        }

        if (isSplitDetailsSheet) {
            if (text === 'date') {
                return 96;
            }
            if (text === 'original currency') {
                return 112;
            }
            if (text === 'currency') {
                return 72;
            }
        }

        if (text.indexOf('date') >= 0 || text.indexOf('time') >= 0 || text.indexOf('posted') >= 0 || text.indexOf('generated') >= 0 || text.indexOf('download') >= 0 || text.indexOf('month') >= 0 || text.indexOf('year') >= 0) {
            return 160;
        }
        if (text.indexOf('description') >= 0 || text.indexOf('remarks') >= 0 || text.indexOf('remark') >= 0 || text.indexOf('note') >= 0 || text.indexOf('comment') >= 0 || text.indexOf('memo') >= 0) {
            return 240;
        }
        if (text.indexOf('fx rate') >= 0 || text.indexOf('exchange rate') >= 0 || text.indexOf('amount') >= 0 || text.indexOf('rate') >= 0 || text.indexOf('price') >= 0 || text.indexOf('percent') >= 0 || text.indexOf('percentage') >= 0 || text.indexOf('%') >= 0) {
            return 120;
        }
        if (text.indexOf('id') >= 0 || text.indexOf('code') >= 0 || text.indexOf('no.') >= 0 || text.indexOf('number') >= 0) {
            return 110;
        }
        return 140;
    }

    formatExcelSerialDate(serialValue) {
        const numericValue = Number(serialValue);
        if (isNaN(numericValue)) {
            return String(serialValue);
        }

        const baseDate = moment('1899-12-30T00:00:00');
        const date = baseDate.clone().add(numericValue, 'days');
        return date.format('YYYY-MM-DD');
    }

    formatPreviewCellValue(value, headerValue) {
        if (value === null || value === undefined || value === '') {
            return '';
        }

        if (moment.isMoment(value)) {
            return value.format('YYYY-MM-DD');
        }

        if (value instanceof Date) {
            return moment(value).format('YYYY-MM-DD');
        }

        const headerText = String(headerValue || '');
        const normalizedHeader = headerText.trim().toLowerCase();
        if (normalizedHeader === 'fx report month' || normalizedHeader.indexOf('fx report month') >= 0) {
            return String(value);
        }

        const numericValue = typeof value === 'number' ? value : (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.trim()) ? Number(value) : null);

        if (numericValue !== null) {
            if (this.isDateLikeHeader(headerText) && numericValue > 1000) {
                return this.formatExcelSerialDate(numericValue);
            }

            if (this.isPercentageHeader(headerText)) {
                const percentValue = numericValue <= 1 ? numericValue * 100 : numericValue;
                return `${percentValue.toFixed(2)}%`;
            }

            if (this.isFxRateHeader(headerText)) {
                return numericValue.toFixed(2);
            }

            return String(Math.round(numericValue));
        }

        return String(value);
    }

    getFilePeriodKey(file) {
        const parsedRange = this.parseDateRangeFromText(file?.recordPeriod);
        const start = parsedRange ? parsedRange.start : moment(file?.recordStartDate);
        const end = parsedRange ? parsedRange.end : moment(file?.recordEndDate);
        if (!start.isValid() || !end.isValid()) {
            return file?.displayRecordPeriod || file?.recordPeriod || file?.fileName || '';
        }

        return `${start.format('YYYY-MM-DD')}|${end.format('YYYY-MM-DD')}`;
    }

    getVersionNumber(versionValue) {
        const match = String(versionValue ?? '').match(/\d+/);
        return match ? Number(match[0]) : null;
    }

    toComparableTimestamp(value) {
        if (!value) {
            return null;
        }

        const raw = String(value).trim();
        const utcWithSeconds = moment.utc(raw, "YYYY-MM-DD HH:mm:ss [UTC]", true);
        if (utcWithSeconds.isValid()) {
            return utcWithSeconds.valueOf();
        }

        const utcNoSeconds = moment.utc(raw, "YYYY-MM-DD HH:mm [UTC]", true);
        if (utcNoSeconds.isValid()) {
            return utcNoSeconds.valueOf();
        }

        const local = moment(raw, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm"], true);
        if (local.isValid()) {
            return local.valueOf();
        }

        const fallback = moment(raw);
        return fallback.isValid() ? fallback.valueOf() : null;
    }

    isLatestFile(candidate, existing) {
        const candidateVersion = this.getVersionNumber(candidate?.version);
        const existingVersion = this.getVersionNumber(existing?.version);
        if (candidateVersion !== null && existingVersion !== null && candidateVersion !== existingVersion) {
            return candidateVersion > existingVersion;
        }

        const candidateGeneratedAt = this.toComparableTimestamp(candidate?.reportGeneratedDateRaw || candidate?.reportGeneratedDate);
        const existingGeneratedAt = this.toComparableTimestamp(existing?.reportGeneratedDateRaw || existing?.reportGeneratedDate);
        if (candidateGeneratedAt !== null && existingGeneratedAt !== null && candidateGeneratedAt !== existingGeneratedAt) {
            return candidateGeneratedAt > existingGeneratedAt;
        }

        const candidateDownloadedAt = this.toComparableTimestamp(candidate?.downloadDateRaw || candidate?.downloadDate);
        const existingDownloadedAt = this.toComparableTimestamp(existing?.downloadDateRaw || existing?.downloadDate);
        if (candidateDownloadedAt !== null && existingDownloadedAt !== null && candidateDownloadedAt !== existingDownloadedAt) {
            return candidateDownloadedAt > existingDownloadedAt;
        }

        return false;
    }

    getMonthlyRange(dateValue) {
        const month = moment(dateValue);
        return {
            recordStartDate: month.clone().startOf('month').toDate(),
            recordEndDate: month.clone().endOf('month').toDate()
        };
    }

    getQuarterlyRange(quarter, year) {
        const start = moment().year(year).quarter(quarter).startOf('quarter');
        return {
            recordStartDate: start.clone().toDate(),
            recordEndDate: start.clone().endOf('quarter').toDate()
        };
    }

    getYearlyRange(year) {
        const start = moment().year(year).startOf('year');
        return {
            recordStartDate: start.clone().toDate(),
            recordEndDate: start.clone().endOf('year').toDate()
        };
    }

    setPeriodType(periodType) {
        const { selectedMonth, selectedQuarter, selectedQuarterYear, selectedYear } = this.state;
        let nextRange = this.getMonthlyRange(selectedMonth);

        if (periodType === 'quarterly') {
            nextRange = this.getQuarterlyRange(selectedQuarter, selectedQuarterYear);
        } else if (periodType === 'yearly') {
            nextRange = this.getYearlyRange(selectedYear);
        }

        this.setState({
            periodType,
            ...nextRange
        });
    }

    handleMonthChange(date) {
        if (!date) {
            return;
        }

        const month = moment(date);
        this.setState({
            selectedMonth: date,
            selectedQuarter: month.quarter(),
            selectedQuarterYear: month.year(),
            selectedYear: month.year(),
            ...this.getMonthlyRange(date)
        });
    }

    handleQuarterChange(fieldName, value) {
        const parsedValue = Number(value);
        if (!parsedValue) {
            return;
        }

        const nextQuarter = fieldName === 'quarter' ? parsedValue : this.state.selectedQuarter;
        const nextQuarterYear = fieldName === 'year' ? parsedValue : this.state.selectedQuarterYear;
        this.setState({
            selectedQuarter: nextQuarter,
            selectedQuarterYear: nextQuarterYear,
            selectedYear: nextQuarterYear,
            ...this.getQuarterlyRange(nextQuarter, nextQuarterYear)
        });
    }

    handleYearChange(value) {
        const parsedYear = Number(value);
        if (!parsedYear) {
            return;
        }

        this.setState({
            selectedYear: parsedYear,
            selectedQuarterYear: parsedYear,
            ...this.getYearlyRange(parsedYear)
        });
    }

    getHistory() {
        this.setState({ isLoadingFiles: true });
        expenseBALService.getHistory().then(data => {
            const latestFilesByPeriod = {};
            (data?.files ?? []).forEach(file => {
                const mappedFile = {
                    ...file,
                    reportGeneratedDateRaw: file.reportGeneratedDate,
                    downloadDateRaw: file.downloadDate,
                    displayRecordPeriod: this.formatRecordPeriodDisplay(file.recordPeriod, file.recordStartDate, file.recordEndDate),
                    downloadDate: this.formatHongKongDateTime(file.downloadDate),
                    reportGeneratedDate: this.formatHongKongDateTime(file.reportGeneratedDate)
                };
                const periodKey = this.getFilePeriodKey(mappedFile);
                const existing = latestFilesByPeriod[periodKey];
                if (!existing || this.isLatestFile(mappedFile, existing)) {
                    latestFilesByPeriod[periodKey] = mappedFile;
                }
            });

            const files = Object.values(latestFilesByPeriod).sort((a, b) => {
                const left = this.toComparableTimestamp(a?.reportGeneratedDateRaw || a?.downloadDateRaw);
                const right = this.toComparableTimestamp(b?.reportGeneratedDateRaw || b?.downloadDateRaw);
                if (left !== null && right !== null) {
                    return right - left;
                }

                return String(b?.fileName || '').localeCompare(String(a?.fileName || ''));
            });

            this.setState({
                files: files,
                isLoadingFiles: false
            });
        }, error => {
            this.setState({
                isLoadingFiles: false,
                showMessage: true,
                errorList: [error],
                messageHeader: 'Error while retrieving expense report history:'
            });
        });
    }

    generate() {
        const { periodType, recordStartDate, recordEndDate } = this.state;
        const start = moment(recordStartDate);
        const end = moment(recordEndDate);

        if (!start.isValid() || !end.isValid()) {
            this.setState({
                showMessage: true,
                errorList: ['Please select a valid records period.'],
                messageHeader: 'Validation error:'
            });
            return;
        }

        if (start.isAfter(end)) {
            this.setState({
                showMessage: true,
                errorList: ['Record start date cannot be after record end date.'],
                messageHeader: 'Validation error:'
            });
            return;
        }

        this.setState({ isGenerating: true });
        expenseBALService.generateReport({
            recordStartDate: start.format("YYYY-MM-DD"),
            recordEndDate: end.format("YYYY-MM-DD"),
            periodType: periodType
        }).then(result => {
            const downloadFileName = this.getReportFileName(periodType, recordStartDate, result.fileName);
            const periodDisplay = this.formatRecordPeriodDisplay(
                `${start.format('YYYY-MM-DD')} to ${end.format('YYYY-MM-DD')}`,
                start,
                end
            );
            this.buildExcelPreview(result.data).then((previewData) => {
                this.setState({
                    isGenerating: false,
                    selectedHistoryFileName: null,
                    pendingDownload: {
                        source: 'generated',
                        periodType: periodType,
                        originalFileName: result.fileName,
                        displayFileName: downloadFileName,
                        displayRecordPeriod: periodDisplay,
                        version: 'Latest',
                        recordStartDate: start.format('YYYY-MM-DD'),
                        recordEndDate: end.format('YYYY-MM-DD'),
                        reportGeneratedDate: moment().format('YYYY-MM-DD HH:mm'),
                        blobData: result.data,
                        previewData: previewData,
                        previewSheetIndex: Math.max((previewData.sheets || []).length - 1, 0),
                        sourceData: {
                            periodType: periodType,
                            recordStartDate: start.format('YYYY-MM-DD'),
                            recordEndDate: end.format('YYYY-MM-DD'),
                            fileName: result.fileName,
                            displayFileName: downloadFileName,
                            displayRecordPeriod: periodDisplay,
                            version: 'Latest'
                        }
                    }
                });
                this.getHistory();
            }, () => {
                this.setState({
                    isGenerating: false,
                    showMessage: true,
                    errorList: ['Unable to build Excel preview.'],
                    messageHeader: 'Preview error:'
                });
            });
        }, error => {
            this.setState({
                isGenerating: false,
                showMessage: true,
                errorList: [error],
                messageHeader: 'Error while generating expense report:'
            });
        });
    }

    openHistoryPreview(file) {
        const recordPeriodText = file.displayRecordPeriod || file.recordPeriod;
        this.setState({ isLoadingPreview: true, selectedHistoryFileName: file.fileName });
        expenseBALService.downloadReport(file.fileName).then(result => {
            this.buildExcelPreview(result.data).then((previewData) => {
                this.setState({
                    isLoadingPreview: false,
                    pendingDownload: {
                        source: 'history',
                        periodType: this.getPeriodTypeFromText(recordPeriodText),
                        originalFileName: file.fileName,
                        displayFileName: this.getHistoryReportFileName(file, file.fileName),
                        displayRecordPeriod: recordPeriodText,
                        version: file.version || '-',
                        recordStartDate: file.recordStartDate,
                        recordEndDate: file.recordEndDate,
                        downloadDate: file.downloadDate,
                        reportGeneratedDate: file.reportGeneratedDate || '-',
                        blobData: result.data,
                        previewData: previewData,
                        previewSheetIndex: Math.max((previewData.sheets || []).length - 1, 0),
                        sourceData: file
                    }
                });
            }, () => {
                this.setState({
                    isLoadingPreview: false,
                    showMessage: true,
                    errorList: ['Unable to build Excel preview.'],
                    messageHeader: 'Preview error:'
                });
            });
        }, error => {
            this.setState({
                isLoadingPreview: false,
                showMessage: true,
                errorList: [error],
                messageHeader: 'Error while downloading expense report:'
            });
        });
    }

    clearDownloadPreview() {
        this.setState({ pendingDownload: null, selectedHistoryFileName: null });
    }

    confirmDownloadPreview() {
        const { pendingDownload } = this.state;
        if (!pendingDownload) {
            return;
        }

        if (pendingDownload.source === 'generated') {
            fileDownload(pendingDownload.blobData, pendingDownload.displayFileName);
            this.setState({ pendingDownload: null });
            return;
        }

        this.setState({ isDownloadingPreview: true });
        const blobToDownload = pendingDownload.blobData;
        if (blobToDownload) {
            fileDownload(blobToDownload, pendingDownload.displayFileName || pendingDownload.originalFileName);
            this.setState({ isDownloadingPreview: false, pendingDownload: null });
            return;
        }

        expenseBALService.downloadReport(pendingDownload.originalFileName).then(result => {
            fileDownload(result.data, pendingDownload.displayFileName || result.fileName);
            this.setState({ isDownloadingPreview: false, pendingDownload: null });
        }, error => {
            this.setState({
                isDownloadingPreview: false,
                showMessage: true,
                errorList: [error],
                messageHeader: 'Error while downloading expense report:'
            });
        });
    }

    render() {
        const {
            periodType,
            selectedMonth,
            selectedQuarter,
            selectedQuarterYear,
            selectedYear,
            recordStartDate,
            recordEndDate,
            files,
            isGenerating,
            isLoadingFiles,
            isLoadingPreview,
            isDownloadingPreview,
            pendingDownload,
            selectedHistoryFileName
        } = this.state;

        const selectorBtnStyle = {
            border: '1px solid #d0d5dd',
            background: '#fff',
            color: '#344054',
            borderRadius: '6px',
            padding: '6px 12px',
            marginRight: '10px',
            fontSize: '13px',
            cursor: 'pointer'
        };
        const activeSelectorBtnStyle = {
            ...selectorBtnStyle,
            border: '1px solid #1756af',
            background: '#eaf2ff',
            color: '#1756af',
            fontWeight: 600
        };
        const selectorInputStyle = {
            border: '1px solid #d0d5dd',
            borderRadius: '6px',
            padding: '7px 10px',
            minWidth: '130px',
            marginRight: '10px'
        };
        const currentYear = moment().year();
        const selectableYears = [];
        for (let year = currentYear + 1; year >= currentYear - 24; year -= 1) {
            selectableYears.push(year);
        }

        const previewTitle = this.getPreviewTitle(pendingDownload);
        const previewRows = this.getPreviewRows(pendingDownload);
        const tabButtonStyle = {
            border: '1px solid #d0d5dd',
            background: '#fff',
            color: '#344054',
            borderRadius: '6px',
            width: '92px',
            height: '30px',
            padding: '0 8px',
            fontSize: '12px',
            lineHeight: '28px',
            cursor: 'pointer',
            textAlign: 'center',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        };
        const activeTabButtonStyle = {
            ...tabButtonStyle,
            border: '1px solid #1756af',
            background: '#eaf2ff',
            color: '#1756af',
            fontWeight: 600
        };

        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', minHeight: 0 }}>
                {this.state.showMessage ? (
                    <ToastMessage
                        header={this.state.messageHeader}
                        errorList={this.state.errorList}
                        closeMessage={this.closeMessage}
                    />
                ) : <div />}

                <div style={{ padding: '8px 16px' }}>
                    <h4><u>Expense Report</u></h4>
                </div>

                <div style={{ display: 'flex', flex: '1 1 auto', minHeight: 0, gap: '14px', padding: '0 16px 16px 16px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', flex: '2 1 0', minWidth: 0, gap: '14px', minHeight: 0 }}>
                        <div style={{ border: '1px solid #d0d5dd', borderRadius: '8px', padding: '10px 12px', backgroundColor: '#fff', boxShadow: '0 1px 2px rgba(16,24,40,0.06)' }}>
                            <div style={{ marginBottom: '8px', color: '#344054' }}>
                                Need to input or verify expense records? Please go to&nbsp;
                                <a href="https://new.expensify.com/" target="_blank" rel="noopener noreferrer">
                                    Expensify
                                </a>.
                            </div>
                            <div style={{ marginBottom: '8px', color: '#475467', fontSize: '12px', fontWeight: 600 }}>Report Period</div>
                            <div style={{ marginBottom: '8px' }}>
                                <button type="button" style={periodType === 'monthly' ? activeSelectorBtnStyle : selectorBtnStyle} onClick={() => this.setPeriodType('monthly')}>Monthly</button>
                                <button type="button" style={periodType === 'quarterly' ? activeSelectorBtnStyle : selectorBtnStyle} onClick={() => this.setPeriodType('quarterly')}>Quarterly</button>
                                <button type="button" style={periodType === 'yearly' ? activeSelectorBtnStyle : selectorBtnStyle} onClick={() => this.setPeriodType('yearly')}>Yearly</button>
                            </div>
                            <div style={{ marginBottom: '8px' }}>
                                {periodType === 'monthly' ? (
                                    <div style={{ display: 'inline-block', marginRight: '24px', marginBottom: '8px' }}>
                                        <div style={{ marginBottom: '4px', color: '#475467', fontSize: '12px', fontWeight: 600 }}>Month</div>
                                        <DatePicker
                                            dateFormat="MM/yyyy"
                                            selected={selectedMonth}
                                            onChange={this.handleMonthChange}
                                            autoComplete="off"
                                            placeholderText="Select month"
                                            dropdownMode="select"
                                            scrollableYearDropdown
                                            yearDropdownItemNumber={25}
                                            showMonthYearPicker
                                            showYearDropdown
                                            popperPlacement="bottom-start"
                                            showPopperArrow={false}
                                            calendarClassName="expense-date-calendar"
                                        />
                                    </div>
                                ) : null}
                                {periodType === 'quarterly' ? (
                                    <div style={{ display: 'inline-block', marginRight: '24px', marginBottom: '8px' }}>
                                        <div style={{ marginBottom: '4px', color: '#475467', fontSize: '12px', fontWeight: 600 }}>Quarter</div>
                                        <select value={selectedQuarter} style={selectorInputStyle} onChange={(event) => this.handleQuarterChange('quarter', event.target.value)}>
                                            <option value={1}>Q1 (Jan - Mar)</option>
                                            <option value={2}>Q2 (Apr - Jun)</option>
                                            <option value={3}>Q3 (Jul - Sep)</option>
                                            <option value={4}>Q4 (Oct - Dec)</option>
                                        </select>
                                        <select value={selectedQuarterYear} style={selectorInputStyle} onChange={(event) => this.handleQuarterChange('year', event.target.value)}>
                                            {selectableYears.map((year) => <option key={`quarter_year_${year}`} value={year}>{year}</option>)}
                                        </select>
                                    </div>
                                ) : null}
                                {periodType === 'yearly' ? (
                                    <div style={{ display: 'inline-block', marginRight: '24px', marginBottom: '8px' }}>
                                        <div style={{ marginBottom: '4px', color: '#475467', fontSize: '12px', fontWeight: 600 }}>Year</div>
                                        <select value={selectedYear} style={selectorInputStyle} onChange={(event) => this.handleYearChange(event.target.value)}>
                                            {selectableYears.map((year) => <option key={`year_${year}`} value={year}>{year}</option>)}
                                        </select>
                                    </div>
                                ) : null}
                            </div>
                            <div style={{ marginBottom: '8px', color: '#344054', fontSize: '13px' }}>
                                Selected record period: <b>{moment(recordStartDate).format('YYYY-MM-DD')}</b> to <b>{moment(recordEndDate).format('YYYY-MM-DD')}</b>
                            </div>
                            <button className="blueB-no-margin" onClick={this.generate} disabled={isGenerating}>
                                {isGenerating ? 'Preparing Preview...' : 'Generate Preview'}
                            </button>
                        </div>

                        <div style={{ flex: '1 1 auto', minHeight: 0, border: '1px solid #babfc7', backgroundColor: '#fff', overflow: 'auto' }}>
                            <div style={{ padding: '8px 10px' }}><b>Previous Files in S3</b></div>
                            {isLoadingFiles ? (
                                <div style={{ padding: '8px 10px' }}>Loading...</div>
                            ) : files?.length > 0 ? (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ border: '1px solid #dee2e6', padding: '6px' }}>Record Period</th>
                                            <th style={{ border: '1px solid #dee2e6', padding: '6px' }}>Download Date</th>
                                            <th style={{ border: '1px solid #dee2e6', padding: '6px' }}>Version</th>
                                            <th style={{ border: '1px solid #dee2e6', padding: '6px' }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {files.map((file, index) => (
                                            <tr
                                                key={`${file.fileName}_${index}`}
                                                style={selectedHistoryFileName === file.fileName ? { backgroundColor: '#eaf2ff' } : null}
                                            >
                                                <td style={{ border: '1px solid #dee2e6', padding: '5px 6px' }}>{file.displayRecordPeriod || file.recordPeriod}</td>
                                                <td style={{ border: '1px solid #dee2e6', padding: '5px 6px' }}>{file.downloadDate}</td>
                                                <td style={{ border: '1px solid #dee2e6', padding: '5px 6px' }}>{file.version}</td>
                                                <td style={{ border: '1px solid #dee2e6', padding: '5px 6px' }}>
                                                    <button className="blueB-no-margin" style={{ padding: '3px 6px', minWidth: '58px', fontSize: '11px', lineHeight: '1.2' }} onClick={() => this.openHistoryPreview(file)}>Preview</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div style={{ padding: '8px 10px' }}>No expense report files found in S3.</div>
                            )}
                        </div>
                    </div>

                    <div style={{ flex: '3 1 0', minWidth: 0, minHeight: 0, border: '1px solid #d0d5dd', borderRadius: '8px', padding: '10px 12px', backgroundColor: '#f8fafc', boxShadow: '0 1px 2px rgba(16,24,40,0.06)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <div style={{ color: '#101828', fontWeight: 600 }}>
                                <div>{previewTitle}</div>
                                {pendingDownload ? (
                                    <div style={{ marginTop: '2px', color: '#475467', fontSize: '12px', fontWeight: 400 }}>
                                        {pendingDownload.displayRecordPeriod ? `Record Period: ${pendingDownload.displayRecordPeriod}` : null}
                                    </div>
                                ) : null}
                            </div>
                            {pendingDownload ? (
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button type="button" style={{ ...selectorBtnStyle, marginRight: 0, padding: '4px 8px', fontSize: '12px' }} onClick={this.clearDownloadPreview} disabled={isDownloadingPreview}>
                                        Clear
                                    </button>
                                    <button className="blueB-no-margin" style={{ padding: '4px 8px', minWidth: '72px', fontSize: '13px', margin: 0 }} onClick={this.confirmDownloadPreview} disabled={isDownloadingPreview}>
                                        {isDownloadingPreview ? 'Downloading...' : 'Download'}
                                    </button>
                                </div>
                            ) : null}
                        </div>
                        {isLoadingPreview ? (
                            <div style={{ padding: '12px 4px', color: '#475467' }}>Loading Excel preview...</div>
                        ) : pendingDownload ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minHeight: 0, flex: '1 1 auto', alignItems: 'stretch' }}>
                                {pendingDownload.previewData && pendingDownload.previewData.sheetNames && pendingDownload.previewData.sheetNames.length > 1 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', flex: '0 0 auto' }}>
                                        {pendingDownload.previewData.sheetNames.map((sheetName, index) => (
                                            <button
                                                key={sheetName}
                                                type="button"
                                                onClick={() => this.setPreviewSheetIndex(index)}
                                                style={index === (pendingDownload.previewSheetIndex || 0) ? activeTabButtonStyle : tabButtonStyle}
                                            >
                                                {sheetName}
                                            </button>
                                        ))}
                                    </div>
                                ) : null}
                                <div style={{ border: '1px solid #d0d5dd', borderRadius: '8px', background: '#fff', overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0, flex: '1 1 auto', marginTop: 0 }}>
                                    <div style={{ padding: '8px 10px', borderBottom: '1px solid #e4e7ec', fontSize: '13px', fontWeight: 600, color: '#101828' }}>Preview</div>
                                    {
                                        pendingDownload.previewData && pendingDownload.previewData.sheets && pendingDownload.previewData.sheets.length > 0 ?
                                            (() => {
                                                const sheet = this.getActivePreviewSheet(pendingDownload);
                                                const rows = sheet ? sheet.rows : [];
                                                const headerRowIndex = sheet && sheet.headerRowIndex !== undefined ? sheet.headerRowIndex : 0;
                                                const headerRow = rows[headerRowIndex] || [];
                                                const preHeaderRows = headerRowIndex > 0 ? rows.slice(0, headerRowIndex) : [];
                                                const visibleColumns = [];
                                                headerRow.forEach((headerValue, colIndex) => {
                                                    if (!this.shouldHidePreviewColumn(headerValue)) {
                                                        visibleColumns.push({ index: colIndex, header: headerValue });
                                                    }
                                                });
                                                if (visibleColumns.length === 0) {
                                                    const maxCols = rows.reduce((max, row) => Math.max(max, row.length), 0);
                                                    for (let i = 0; i < maxCols; i += 1) {
                                                        if (!this.shouldHidePreviewColumn(headerRow[i])) {
                                                            visibleColumns.push({ index: i, header: headerRow[i] });
                                                        }
                                                    }
                                                }
                                                const dataRows = rows.length > headerRowIndex + 1 ? rows.slice(headerRowIndex + 1) : [];
                                                const columnWidths = visibleColumns.map((column) => this.getPreviewColumnWidth(column.header, sheet ? sheet.name : ''));
                                                return (
                                                    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: '1 1 auto', overflow: 'hidden' }}>
                                                        <div style={{ overflow: 'auto', minHeight: 0, flex: '1 1 auto' }}>
                                                            {preHeaderRows.length > 0 ? (
                                                                <div style={{ padding: '8px 10px', borderBottom: '1px solid #eef2f6', fontSize: '13px', color: '#344054', background: '#fcfcfd' }}>
                                                                    {preHeaderRows.map((row, index) => {
                                                                        const text = row.filter((cell) => cell !== null && cell !== undefined && cell !== '').map((cell) => this.formatPreviewCellValue(cell, '')).join(' | ');
                                                                        if (!text) {
                                                                            return null;
                                                                        }
                                                                        return <div key={`preheader_${index}`}>Row {index + 1}: {text}</div>;
                                                                    })}
                                                                </div>
                                                            ) : null}
                                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                                <thead>
                                                                    <tr>
                                                                        {visibleColumns.map((column, columnPos) => (
                                                                            <th key={column.index} style={{ borderTop: '1px solid #eef2f6', borderRight: '1px solid #eef2f6', padding: '4px 8px', background: '#f9fafb', fontSize: '13px', textAlign: 'left', whiteSpace: 'nowrap', minWidth: columnWidths[columnPos] + 'px', width: columnWidths[columnPos] + 'px' }}>
                                                                                {column.header === undefined || column.header === null || column.header === '' ? `Column ${column.index + 1}` : String(column.header)}
                                                                            </th>
                                                                        ))}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {dataRows.length > 0 ? (
                                                                        dataRows.map((row, rowIndex) => (
                                                                            <tr key={`sheet_row_${rowIndex}`}>
                                                                                {visibleColumns.map((column, columnPos) => (
                                                                                    <td key={`cell_${rowIndex}_${column.index}`} style={{ borderTop: '1px solid #eef2f6', borderRight: '1px solid #eef2f6', padding: '4px 8px', color: '#101828', whiteSpace: 'pre-wrap', wordBreak: 'break-word', verticalAlign: 'top', fontSize: '13px', minWidth: columnWidths[columnPos] + 'px', width: columnWidths[columnPos] + 'px' }}>
                                                                                        {this.formatPreviewCellValue(row[column.index], column.header)}
                                                                                    </td>
                                                                                ))}
                                                                            </tr>
                                                                        ))
                                                                    ) : (
                                                                        <tr>
                                                                            <td colSpan={Math.max(visibleColumns.length, 1)} style={{ borderTop: '1px solid #eef2f6', padding: '14px 10px', color: '#667085', fontSize: '13px', textAlign: 'center' }}>
                                                                                No record found in the selected preview period.
                                                                            </td>
                                                                        </tr>
                                                                    )}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                );
                                            })()
                                            :
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <tbody>
                                                    {previewRows.map((row) => (
                                                        <tr key={row[0]}>
                                                            <td style={{ width: '34%', borderTop: '1px solid #eef2f6', padding: '4px 8px', color: '#475467', verticalAlign: 'top', fontSize: '13px' }}>{row[0]}</td>
                                                            <td style={{ borderTop: '1px solid #eef2f6', padding: '4px 8px', color: '#101828', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '13px' }}>{String(row[1])}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                    }
                                </div>
                            </div>
                        ) : (
                            <div style={{ color: '#475467', fontSize: '13px', padding: '4px 2px' }}>
                                Select a file from the history list or generate a new preview.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default ExpenseDashboard;
